import { Box, Button, Container, Grid2 as Grid, Tooltip, Typography } from "@mui/material"
import { red } from "@mui/material/colors"
import { useContext, useEffect } from "react"
import { Link } from "react-router-dom"
import { useFindReviewStatus } from "../../api/MissionEditApi"
import { ButtonVariant } from "../../assets/theme"
import { NexAlert } from "../../components/Alert/Alert"
import { NexAutocomplete } from "../../components/Fields/Autocomplete"
import { LinearProgressWithLabel } from "../../components/LinearProgressWithLabel/LinearProgressWithLabel"
import { ProjectTitle } from "../../components/Titles/ProjectTitle"
import { Label } from "../../components/Typography/Typography"
import { useNexusProjectNumber } from "../../hooks/useNexusParams"
import { useTitle } from "../../hooks/useTitle"
import { MissionReviewStatusType } from "../../models/MissionReview"
import { routes } from "../../routing/NexRoutes"
import { RootContext } from "../../store/Context"
import { ToolbarOptions } from "../../store/types/AuthTypes"
import { MissionNameType } from "../../store/types/MissionsTypes"
import { Types } from "../../store/types/Types"
import { ReviewStatusText, getPercentComplete, getStatusDesign } from "../../utils/MissionStatusDesigns"
import { StringUtils } from "../../utils/StringUtils"
import { useAppErrorStore } from "../../zstore/AppErrorStore"
import { useAuthStore } from "../../zstore/AuthStore"
import { useMissionReviewStore } from "../../zstore/MissionReviewStore"
import { useStyles } from "./styles"

export const MissionSelection = () => {
    useTitle("Mission Select")
    const { classes } = useStyles()
    const projectNumber = useNexusProjectNumber()
    const { state, dispatch } = useContext(RootContext)
    const appError = useAppErrorStore((state) => state.appError)
    const clearError = useAppErrorStore((state) => state.clear)
    const { selectedMissionId } = state.missions

    const checkToolbarAccess = useAuthStore((state) => state.checkToolbarAccess)
    const userCanQC = checkToolbarAccess(ToolbarOptions.Flightline_QC_Review)

    const setMissionName = useMissionReviewStore((state) => state.setMissionName)

    const reviewStatusListResponse = useFindReviewStatus(projectNumber)
    const reviewStatusList = reviewStatusListResponse.data ?? []

    const selectedMission = reviewStatusList.find((s) => s.mission_id === selectedMissionId) || null

    const missionLinks: {
        to: string
        label: string
        disabled: boolean
        variant?: ButtonVariant
        tooltipText?: string
    }[] = [
        {
            label: "Edit Mission Log",
            to: `/${projectNumber}${routes.missionEditor}/edit/${selectedMissionId}`,
            disabled: selectedMission == null,
            variant: "contained",
        },
    ]

    if (userCanQC) {
        missionLinks.unshift({
            label: "Mission Review",
            to: `/${projectNumber}${routes.missionReview}/${selectedMissionId}`,
            disabled: selectedMission === null || !selectedMission.event_ct_total,
            variant: "outlined",
            tooltipText: selectedMission === null || selectedMission.event_ct_total ? "" : "No Review Required",
        })
    }

    useEffect(() => {
        clearError()
        dispatch({ type: Types.Missions_ResetState })
    }, [])

    const handleChange = (value: number | null, name?: string) => {
        setMissionName(name)

        dispatch({
            type: Types.Missions_SelectMissionId,
            payload: { selectedMissionId: value },
        })
    }

    return (
        <Container maxWidth="md" className={classes.root}>
            <ProjectTitle projectNumber={projectNumber} />

            {reviewStatusListResponse.error && (
                <NexAlert>
                    {StringUtils.parseWarningsFromErrors(reviewStatusListResponse.error).map((error) => (
                        <li key={error}>{error}</li>
                    ))}
                </NexAlert>
            )}

            <Label gutterBottom="sm">Select an Existing Mission to Edit or Review</Label>
            <NexAutocomplete
                sx={{ minHeight: 80, mb: 2 }}
                disabled={reviewStatusListResponse.isLoading}
                TextFieldProps={{ helperText: selectedMission && getHelperText(selectedMission) }}
                value={reviewStatusList.find((s) => s.mission_id === selectedMissionId) || null}
                options={reviewStatusList}
                getOptionLabel={(opt: MissionNameType) => opt.mission_name ?? ""}
                renderOption={(props, opt: MissionReviewStatusType) => {
                    const status = getStatusDesign(opt)

                    return (
                        <li {...props} key={opt.mission_id}>
                            <Box sx={{ display: "flex", gap: { xs: 2, sm: 4, md: 8 }, width: "100%" }}>
                                <Typography sx={{ flexBasis: "30%", color: status.color }}>
                                    {opt.mission_name}
                                </Typography>
                                <Typography
                                    sx={{
                                        flexBasis: opt.event_ct_total ? "20%" : "auto",
                                        color: status.color,
                                    }}
                                >
                                    {status.text}
                                </Typography>
                                {!!opt.event_ct_total && (
                                    <LinearProgressWithLabel
                                        color={status.text === ReviewStatusText.UNDER_REVIEW ? "secondary" : "primary"}
                                        sx={{ bgColor: red[50] }}
                                        value={getPercentComplete(opt)}
                                        percentAtEnd
                                    />
                                )}
                            </Box>
                        </li>
                    )
                }}
                onChange={(_, value) => handleChange(value?.mission_id ?? null, value?.mission_name)}
                fullWidth
                loading={reviewStatusListResponse.isLoading}
            />

            {appError && (
                <NexAlert animate onClose={clearError}>
                    {appError}
                </NexAlert>
            )}

            <Grid container justifyContent="flex-end" spacing={2}>
                <Grid sx={{ mr: "auto" }}>
                    <Button component={Link} to={routes.home} variant="outlined" color="primary" size="large">
                        Back
                    </Button>
                </Grid>
                {missionLinks.map((link) => (
                    <Grid key={link.to}>
                        <Tooltip
                            title={link.tooltipText}
                            arrow
                            slotProps={{
                                popper: {
                                    modifiers: [{ name: "offset", options: { offset: [0, 5] } }],
                                },
                            }}
                        >
                            <span>
                                <Button
                                    component={Link}
                                    to={link.to}
                                    variant={link.variant}
                                    size="large"
                                    disabled={link.disabled}
                                >
                                    {link.label}
                                </Button>
                            </span>
                        </Tooltip>
                    </Grid>
                ))}
            </Grid>
        </Container>
    )
}

const getHelperText = (selectedMission: MissionReviewStatusType) => {
    let text = getStatusDesign(selectedMission).text as string
    if (selectedMission.event_ct_total) {
        text = `${text} - ${getPercentComplete(selectedMission)}%`
    }
    return text
}
